import { Pipe, PipeTransform, Inject, LOCALE_ID, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

/**
 * @whatItDoes Formats a date according to locale rules (using angular2 date pipe), but allows for verbose labels for 12:00 am and 12:00 pm (midnightLabel and noonLabel)
 * @howToUse `date_expression | timeTranslate[:format:midnightLabel:noonLabel]`
 * @description
 *
 * Where:
 * - `expression` is a date object or a number (milliseconds since UTC epoch) or an ISO string
 * - `format` indicates which date/time components to include. See angular2's date pipe for more information.
 * - `midnightLabel` is the text that will be shown instead of 12:00 AM
 * - `noonLabel` is the text that will be shown instead of 12:00 PM
 *
 * In javascript, only the components specified will be respected (not the ordering,
 * punctuations, ...) and details of the formatting will be dependent on the locale.
 *
 * ### Examples
 *
 * Assuming `dateObj` is (year: 2015, month: 6, day: 15, hour: 21, minute: 43, second: 11)
 * in the _local_ time and locale is 'en-US':
 *
 * ```
 *     {{ dateObj | time-translate:'shortTime':'Midnight':'Noon' }}               // output is '21:43'
 * ```
 */
@Pipe({ name: 'wlTimeTranslate', pure: false, standalone: true })
export class WlTimeTranslatePipe implements PipeTransform {
  public _defaultMidnightLabel: string = '';
  public _defaultNoonLabel: string = '';
  public _lastValue: Date = null;
  public _lastFormat: string = null;
  public _lastMidnightLabel: string = null;
  public _lastNoonLabel: string = null;
  public _lastTransformedTime: string = '';

  public _tranformAfterTranslation: boolean = false;

  constructor(@Inject(LOCALE_ID) public _locale: string, public _translateService: TranslateService, changeDetectorRef: ChangeDetectorRef) {
    this._translateService
      .get(['common.time.midnight-label', 'common.time.noon-label'])
      .toPromise()
      .then(translated => {
        this._defaultMidnightLabel = translated['common.time.midnight-label'];
        this._defaultNoonLabel = translated['common.time.noon-label'];
        this._tranformAfterTranslation = true;
        changeDetectorRef.markForCheck();
      });
  }

  transform(value: Date, format: string, midnightLabel: string, noonLabel: string): string {
    // same input, and translation hasn't changed
    if (
      !this._tranformAfterTranslation &&
      this._lastValue == value &&
      this._lastFormat == format &&
      this._lastMidnightLabel == midnightLabel &&
      this._lastNoonLabel == noonLabel
    )
      return this._lastTransformedTime;

    // have to remember
    this._lastValue = value;
    this._lastFormat = format;
    this._lastMidnightLabel = midnightLabel;
    this._lastNoonLabel = noonLabel;

    // is true if the translation service comes back, so it's a force refresh
    this._tranformAfterTranslation = false;

    if (typeof value == 'string') value = this.convertStringToDate(<string>(<any>value));
    if (!value) return (this._lastTransformedTime = '');
    if (!(value instanceof Date)) return (this._lastTransformedTime = '');

    return (this._lastTransformedTime = this.getTranslatedTime(value, format, midnightLabel, noonLabel));
  }

  /**
   * the work horse
   * @param value
   * @param format
   * @param midnightLabel
   * @param noonLabel
   */
  public getTranslatedTime(value: Date, format: string, midnightLabel: string, noonLabel: string): any {
    if (!value) return '';
    if (!(value instanceof Date)) return '';

    if (!format) format = 'shortTime';

    if (value.getHours() == 0 && value.getMinutes() == 0 && (midnightLabel || this._defaultMidnightLabel))
      return midnightLabel || this._defaultMidnightLabel;

    if (value.getHours() == 12 && value.getMinutes() == 0 && (noonLabel || this._defaultNoonLabel))
      return noonLabel || this._defaultNoonLabel;

    return new DatePipe(this._locale).transform(value, format);
  }

  public convertStringToDate(strDate: string): Date {
    if (!strDate) return null;

    let date: Date = null;
    let matches = strDate.match(/^(\d?\d):(\d\d)$/);

    // simple time string
    if (matches && matches.length) date = new Date(0, 0, 0, parseInt(matches[1]), parseInt(matches[2]));

    return date;
  }
}
